import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';
import '../../../css/Accessibility.scss';
import Utils from '../../services/UtilsService';
import HtmlComponent from '../HtmlComponent';
import useAccessibilityClick from '../../../hooks/useAccessibilityClick';
import { FlyoutOpenState, FlyoutTabs, LessonMode } from '../../../Constants';
import navMenuService from '../../services/NavMenuService';
import { register } from '../../../i18n';
import scoringService from '../../services/ScoringService';
import { isRubricCategoryFullyChecked, isRubricCheckboxScoring } from '../../utils/rubricCheckboxScoring';

const t = register('PanelMenu');
const t2 = register('AriaLabels');

const RubricView = observer(({ lessonElementId }) => {
  const {
    scoringManager,
    navMenuManager,
    lessonManager,
    responseManager,
    userManager
  } = useContext(MobXProviderContext);

  const [buttonLabel, setButtonLabel] = useState(`${t('hideButton')}`);

  const rubric = scoringManager.rubricMap.get(lessonElementId);
  const checkboxMode = rubric && isRubricCheckboxScoring(rubric);
  const scoringUi = lessonManager.playerMode === LessonMode.SCORING &&
    userManager.isTeacher &&
    checkboxMode;

  const hideButton = useRef();

  useEffect(() => {
    const state = responseManager.getLessonElementState(lessonElementId);
    if (state && state.subscore) {
      scoringManager.setSubscore(
        lessonElementId,
        JSON.parse(state.subscore),
        state.isScored
      );
    }
  }, [lessonElementId, responseManager, scoringManager]);

  useAccessibilityClick(hideButton, () => {
    const os = navMenuManager.getFlyoutOpenState(lessonElementId);
    if (os.open === FlyoutOpenState.OPEN) {
      navMenuService.toggleFlyoutPanel(false, FlyoutOpenState.OPEN_WIDE, lessonElementId);
    } else {
      navMenuService.toggleFlyoutPanel(false, FlyoutOpenState.OPEN, lessonElementId);
    }
  });

  const openState = navMenuManager.getFlyoutOpenState(lessonElementId);
  useEffect(() => {
    if (openState && openState?.open === FlyoutOpenState.OPEN_WIDE && openState?.tab === FlyoutTabs.RUBRIC) {
      setButtonLabel(`${t('hideButton')}`);
    } else {
      setButtonLabel(`${t('showButton')}`);
    }
  }, [openState]);

  if (rubric) {
    return (
      <div className='rubric-wrapper'>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {openState?.open === FlyoutOpenState.OPEN_WIDE && openState?.tab === FlyoutTabs.RUBRIC ? 
           'Rubric expanded' : 'Rubric collapsed'}
        </div>
        <div className='rubric-container'>
          <button 
            ref={hideButton} 
            className='hide'
            aria-label={buttonLabel}
            aria-expanded={openState?.open === FlyoutOpenState.OPEN_WIDE && openState?.tab === FlyoutTabs.RUBRIC}
            aria-controls={`${lessonElementId}-rubric-content`}
          >
            {buttonLabel}
          </button>
          <table className='rubric font-content-container' id={`${lessonElementId}-rubric-content`}>
            <thead>
              <tr>
                <th className='no-border font-content-container' />
                {rubric.columns.map((column) => {
                  return (<th key={`rebric_header_${Utils.getKey()}`} className='columnHeader font-content-container'><HtmlComponent htmlStr={column} /></th>);
                })}
                {!checkboxMode ? (
                  <th className='no-border font-content-container'>{t('maxPoints')}</th>
                ) : (
                  <th className='no-border font-content-container rubric-checkbox-header'>{t('rubricCriterionMet')}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rubric.categories.map((category) => {
                return (
                  <tr key={`rebric_row_${Utils.getKey()}`}>
                    <td className='category-label font-content-container'><HtmlComponent htmlStr={category.name} /></td>
                    {category.columns.map((column, clickIndex) => {
                      return (
                        <td key={`rebric_inner_col_${Utils.getKey()}`}
                          className={classNames({
                            'rubric-cell': true,
                            'font-content-container': true,
                            'selected': scoringManager.isSelectedCell(category.categoryId, clickIndex, lessonElementId)
                          })}>
                          <HtmlComponent htmlStr={column} />
                        </td>
                      );
                    })}
                    {!checkboxMode ? (
                      <td className='max-points font-content-container'>{category.maxPoints}</td>
                    ) : (
                      <td className='rubric-checkbox-cell font-content-container'>
                        <input
                          aria-label={t2('rubricCategoryMetCheckbox')}
                          checked={isRubricCategoryFullyChecked(category)}
                          className='rubric-category-checkbox'
                          disabled={!scoringUi}
                          onChange={(e) => {
                            scoringService.setCategoryCheckboxScore(
                              lessonElementId,
                              category.categoryId,
                              e.target.checked
                            );
                          }}
                          type='checkbox'
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  } else { return null; }
});

export default RubricView;
